/**
 * Area `handoff` of the console gateway — over `apps/api/src/routes/handoff/` (design §30.4). Owned by plan-018 unit
 * **API-HO**: the VoiceAgent's handoff setting — on or off, the person's number and the language the person hears the
 * summary in, If-Match on its revision (ADR 0061; owner answers Q7 and Q8). The panel that draws it is WAPP-C's.
 *
 * ```
 *   readVoiceAgentHandoff     `getVoiceAgentHandoff`   GET /v1/agents/{id}/handoff   operational roles; ETag = revision
 *   changeVoiceAgentHandoff   `setVoiceAgentHandoff`   PUT /v1/agents/{id}/handoff   voice_agent.write; If-Match required
 * ```
 *
 * A REFUSAL KEEPS ITS DETAIL. `handoff_unavailable` and `handoff_language_unavailable` carry `error.detail.reason`, and
 * `handoff_destination_refused` carries `error.detail.path_refusal`; the generated `ApiError` drops `detail`, so this
 * slice raises {@link HandoffRefusedError} (an `ApiError`, the same code, status and field) with the detail read, so the
 * panel can say which line refused the number, in the portal's language, rather than echo the API's English sentence.
 *
 * The register is EMPTY: both operations are in the assembled spec. The sandbox half is `../sandbox/handoff.ts`.
 */
import { useQuery, type UseQueryResult } from "@tanstack/react-query";
import { ApiError, assertOk, type WeTalkApiClient } from "@wetalk/api-client";

import type { ConsoleGateway } from "../gateway.js";
import { MalformedResponseError, readArray, readBoolean, readRecord, readText, readWholeNumber } from "../decode.js";
import { useGateway } from "../provider.js";
import type { GatewayGap, SliceContext } from "./slice-context.js";

export const HANDOFF_AVAILABILITY_PATH = ["sip_trunk", "carrier", "browser", "web_chat"] as const;
export type HandoffAvailabilityPath = (typeof HANDOFF_AVAILABILITY_PATH)[number];

export const HANDOFF_AVAILABILITY_STATE = ["available", "not_available_yet", "priority_message", "inbound_only_trunk"] as const;
export type HandoffAvailabilityState = (typeof HANDOFF_AVAILABILITY_STATE)[number];

/** One kind of line the VoiceAgent answers on, and whether a caller on it can be put through to a person. */
export interface HandoffAvailabilityView {
  readonly path: HandoffAvailabilityPath;
  readonly state: HandoffAvailabilityState;
  /** The trunk, on a `sip_trunk` row. */
  readonly sipTrunkId: string | null;
  readonly sipTrunkName: string | null;
}

/** The VoiceAgent's handoff setting (`VoiceAgentHandoff`). */
export interface VoiceAgentHandoffView {
  /** Whether this kind of agent puts callers through to a person at all. */
  readonly offered: boolean;
  readonly enabled: boolean;
  readonly destinationE164: string | null;
  readonly languageCode: string | null;
  /** What a change names in `If-Match`. */
  readonly revision: number;
  /** SIP trunks first, one row per trunk. */
  readonly availability: readonly HandoffAvailabilityView[];
}

/** A change: every field stated; the number and the language may be `null` only while off. */
export interface HandoffSettingChange {
  readonly enabled: boolean;
  readonly destinationE164: string | null;
  readonly languageCode: string | null;
}

/** One available line's reason for refusing the number (`error.detail.path_refusal[]`). */
export interface HandoffPathRefusalView {
  readonly path: "sip_trunk" | "carrier";
  readonly sipTrunkId: string | null;
  readonly refusal: string;
}

/**
 * A handoff refusal with its detail: `reason` for `handoff_unavailable` (`switch_off`, `inbound_only_trunk`,
 * `no_voice_binding`) and `handoff_language_unavailable` (`not_configured`, `framing_missing`); `pathRefusal` for
 * `handoff_destination_refused`.
 */
export class HandoffRefusedError extends ApiError {
  readonly reason: string | null;
  readonly pathRefusal: readonly HandoffPathRefusalView[];
  constructor(status: number, body: unknown, requestId: string | null) {
    super(status, body, requestId);
    const detail = (body as { error?: { detail?: unknown } } | null)?.error?.detail;
    const record = typeof detail === "object" && detail !== null ? (detail as Readonly<Record<string, unknown>>) : {};
    this.reason = typeof record["reason"] === "string" ? record["reason"] : null;
    this.pathRefusal = Array.isArray(record["path_refusal"])
      ? (record["path_refusal"] as readonly unknown[]).map((one, index) => {
          const row = readRecord(one, `error.detail.path_refusal[${String(index)}]`);
          const path = readText(row["path"], "path");
          if (path !== "sip_trunk" && path !== "carrier") throw new MalformedResponseError("path", "sip_trunk or carrier", path);
          return {
            path,
            sipTrunkId: row["sip_trunk_id"] === null ? null : readText(row["sip_trunk_id"], "sip_trunk_id"),
            refusal: readText(row["refusal"], "refusal"),
          };
        })
      : [];
  }
}

declare module "../gateway.js" {
  interface ConsoleGateway {
    /** plan-018 API-HO: the VoiceAgent's handoff setting and where it can hand a caller over. */
    readVoiceAgentHandoff(voiceAgentId: string): Promise<VoiceAgentHandoffView>;
    /**
     * plan-018 API-HO: switch the handoff on or off, with the number and the person's language, naming the revision
     * read. Raises {@link HandoffRefusedError} for a refusal with a detail, the API's `409 revision_stale` when someone
     * changed it first.
     */
    changeVoiceAgentHandoff(voiceAgentId: string, change: HandoffSettingChange, revision: number): Promise<VoiceAgentHandoffView>;
  }
}

/** The methods this slice puts on `ConsoleGateway`. */
export type HandoffGatewaySlice = Pick<ConsoleGateway, "readVoiceAgentHandoff" | "changeVoiceAgentHandoff">;

/** This area's rows of `GATEWAY_GAP`: none — both operations are served. */
export const handoffGap: readonly GatewayGap[] = [];

// ── the wire ─────────────────────────────────────────────────────────────────────────────────────────────

function oneOf<T extends string>(allowed: readonly T[], value: unknown, path: string): T {
  const raw = readText(value, path);
  const found = allowed.find((one) => one === raw);
  if (found === undefined) throw new MalformedResponseError(path, allowed.join(" | "), raw);
  return found;
}

function nullableText(value: unknown, path: string): string | null {
  return value === null ? null : readText(value, path);
}

/** One `VoiceAgentHandoff`. */
export function voiceAgentHandoffOf(wire: unknown, path: string): VoiceAgentHandoffView {
  const data = readRecord(wire, path);
  return {
    offered: readBoolean(data["offered"], `${path}.offered`),
    enabled: readBoolean(data["enabled"], `${path}.enabled`),
    destinationE164: nullableText(data["destination_e164"], `${path}.destination_e164`),
    languageCode: nullableText(data["language_code"], `${path}.language_code`),
    revision: readWholeNumber(data["revision"], `${path}.revision`),
    availability: readArray(data["availability"], `${path}.availability`).map((one, index) => {
      const at = `${path}.availability[${String(index)}]`;
      const row = readRecord(one, at);
      return {
        path: oneOf(HANDOFF_AVAILABILITY_PATH, row["path"], `${at}.path`),
        state: oneOf(HANDOFF_AVAILABILITY_STATE, row["state"], `${at}.state`),
        sipTrunkId: nullableText(row["sip_trunk_id"], `${at}.sip_trunk_id`),
        sipTrunkName: nullableText(row["sip_trunk_name"], `${at}.sip_trunk_name`),
      };
    }),
  };
}

function dataOf(body: unknown, operationId: string): unknown {
  return readRecord(body, operationId)["data"];
}

const REFUSAL_WITH_DETAIL: readonly string[] = ["handoff_unavailable", "handoff_destination_refused", "handoff_language_unavailable"];

export function handoffApiSlice(client: WeTalkApiClient, _context: SliceContext): HandoffGatewaySlice {
  return {
    async readVoiceAgentHandoff(voiceAgentId) {
      const answer = await client.getVoiceAgentHandoff({ path: { voice_agent_id: voiceAgentId } });
      return voiceAgentHandoffOf(dataOf(assertOk(answer), "getVoiceAgentHandoff"), "getVoiceAgentHandoff.data");
    },
    async changeVoiceAgentHandoff(voiceAgentId, change, revision) {
      const answer = await client.setVoiceAgentHandoff({
        path: { voice_agent_id: voiceAgentId },
        body: { enabled: change.enabled, destination_e164: change.destinationE164, language_code: change.languageCode },
        header: { "if-match": `"${String(revision)}"` },
      });
      if (answer.status === 400 || answer.status === 422) {
        const refused = new HandoffRefusedError(answer.status, answer.body, answer.requestId);
        if (REFUSAL_WITH_DETAIL.includes(refused.code)) throw refused;
      }
      return voiceAgentHandoffOf(dataOf(assertOk(answer), "setVoiceAgentHandoff"), "setVoiceAgentHandoff.data");
    },
  };
}

// ── the read the panel uses ──────────────────────────────────────────────────────────────────────────────

/** Query keys of this area, per VoiceAgent. */
export const HANDOFF_QUERY_KEY = {
  voiceAgentHandoff: (voiceAgentId: string) => ["handoff", voiceAgentId] as const,
};

/** The VoiceAgent's handoff setting. A refused or failed read leaves `data` undefined — never a remembered value. */
export function useVoiceAgentHandoff(voiceAgentId: string): UseQueryResult<VoiceAgentHandoffView> {
  const gateway = useGateway();
  return useQuery({
    queryKey: HANDOFF_QUERY_KEY.voiceAgentHandoff(voiceAgentId),
    queryFn: () => gateway.readVoiceAgentHandoff(voiceAgentId),
    retry: false,
  });
}
